import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserProfile, WorkExperience, generateId } from "@/lib/storage";
import { Plus, Trash2 } from "lucide-react";

interface Props {
  profile: UserProfile;
  onChange: (profile: UserProfile) => void;
}

export default function ExperienceStep({ profile, onChange }: Props) {
  const addEntry = () => {
    const entry: WorkExperience = { id: generateId(), company: '', title: '', startDate: '', endDate: '', current: false, description: '' };
    onChange({ ...profile, experience: [...profile.experience, entry] });
  };

  const updateEntry = (id: string, field: keyof WorkExperience, value: string | boolean) => {
    onChange({
      ...profile,
      experience: profile.experience.map(e => e.id === id ? { ...e, [field]: value } : e),
    });
  };

  const removeEntry = (id: string) => {
    onChange({ ...profile, experience: profile.experience.filter(e => e.id !== id) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-lg text-foreground">Work Experience</h2>
          <p className="text-sm text-muted-foreground mt-1">Add your recent positions.</p>
        </div>
        <Button size="sm" variant="outline" onClick={addEntry} className="gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add
        </Button>
      </div>

      {profile.experience.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No experience added yet. Click "Add" to get started.
        </div>
      )}

      {profile.experience.map((exp, i) => (
        <div key={exp.id} className="p-4 rounded-xl bg-muted/50 border border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Position {i + 1}</span>
            <button onClick={() => removeEntry(exp.id)} className="text-destructive hover:text-destructive/80 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Company</Label>
              <Input placeholder="Acme Inc." value={exp.company} onChange={e => updateEntry(exp.id, 'company', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Job Title</Label>
              <Input placeholder="Software Engineer" value={exp.title} onChange={e => updateEntry(exp.id, 'title', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Start Date</Label>
              <Input placeholder="Jan 2022" value={exp.startDate} onChange={e => updateEntry(exp.id, 'startDate', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">End Date</Label>
              <Input placeholder="Present" value={exp.endDate} onChange={e => updateEntry(exp.id, 'endDate', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Textarea placeholder="Describe your role and achievements..." value={exp.description} onChange={e => updateEntry(exp.id, 'description', e.target.value)} className="mt-1 min-h-[60px]" />
          </div>
        </div>
      ))}
    </div>
  );
}
